import React, { useState, useMemo, useEffect } from 'react';
import { useReportStaticTexts } from '../../hooks/useReportStaticTexts';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import PaginationControls from '../branches/PaginationControls';
import 'react-confirm-alert/src/react-confirm-alert.css';

const ReportStaticTextsList = () => {
  const { 
    loading, 
    openEditModal, 
    deleteText, 
    openCreateModal,
    filteredTexts,
    updateFilters,
    filters,
    modules
  } = useReportStaticTexts();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [textsPerPage] = useState(10);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredTexts.length]);
  
  // Get current texts for pagination
  const { currentTexts, totalPages } = useMemo(() => {
    const indexOfLastText = currentPage * textsPerPage;
    const indexOfFirstText = indexOfLastText - textsPerPage;
    const current = filteredTexts.slice(indexOfFirstText, indexOfLastText);
    const total = Math.ceil(filteredTexts.length / textsPerPage);
    return { currentTexts: current, totalPages: total };
  }, [filteredTexts, currentPage, textsPerPage]);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handleSearchChange = (e) => {
    updateFilters({ search: e.target.value });
  };

  const handleModuleChange = (e) => {
    updateFilters({ module: e.target.value });
  };

  const handleDelete = (textId) => {
    confirmAlert({
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this report static text?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => deleteText(textId)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  // Parse text_values JSON
  const parseTextValues = (textValuesStr) => {
    try {
      if (typeof textValuesStr === 'string') {
        return JSON.parse(textValuesStr);
      }
      return textValuesStr || [];
    } catch (error) {
      console.error('Error parsing text values:', error);
      return [];
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Report Static Texts</h1>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <FaPlus /> Add New
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-4 flex-wrap">
        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search by module or report name..."
            value={filters.search}
            onChange={handleSearchChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        {/* Module Filter */}
        <div className="flex-1 min-w-[200px]">
          <select
            value={filters.module}
            onChange={handleModuleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="">All Modules</option>
            {modules.map(module => (
              <option key={module} value={module}>
                {module}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters */}
        {(filters.search || filters.module) && (
          <button
            onClick={() => updateFilters({ search: '', module: '' })}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b-2 border-gray-300">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Module Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Report Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Text Values</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTexts.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      No report static texts found
                    </td>
                  </tr>
                ) : (
                  currentTexts.map((text) => {
                    const textValues = parseTextValues(text.text_values);
                    return (
                      <tr key={text.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-800">
                          <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
                            {text.module_name}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-800">
                          <span className="inline-block bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium">
                            {text.report_name}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="max-w-xs">
                            {textValues.length > 0 ? (
                              <div className="space-y-1">
                                {textValues.map((val, idx) => (
                                  <div key={idx} className="text-xs">
                                    <span className="font-semibold text-gray-700">{val.label}:</span> {val.text.substring(0, 50)}...
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">No text values</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => openEditModal(text)}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Edit"
                            >
                              <FaEdit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(text.id)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Delete"
                            >
                              <FaTrash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredTexts.length > textsPerPage && (
            <div className="mt-6">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {currentTexts.length > 0 ? (currentPage - 1) * textsPerPage + 1 : 0} to {Math.min(currentPage * textsPerPage, filteredTexts.length)} of {filteredTexts.length} results
          </div>
        </>
      )}
    </div>
  );
};

export default ReportStaticTextsList;
